import { O } from '../../../features/ui'
import { TableName } from '../../../tables'
import { toList } from '../../../utils/listUtils'
import { useLoaders } from '../../common'
import { usePurchaseLoaders } from '../../purchases'
import { useSystemStateHooks } from '../../system'
import { Ticket } from '../state/types'
import { useExpenseLoaders } from './expenseLoaders'

export const useTicketLoaders = () => {
    const systemHooks = useSystemStateHooks()

    const loaders = useLoaders()
    const purchaseLoaders = usePurchaseLoaders()
    const expenseLoaders = useExpenseLoaders()

    return (companyId = systemHooks.companyId) => {
        const loadAllTickets = () => {
            const { loadAll } = loaders(companyId)
            return loadAll(TableName.TICKETS) as Promise<Ticket[]>
        }

        const loadTicket = (
            id: string,
            dbRequest = false
        ) => {
            const { load } = loaders(companyId)
            return load(TableName.TICKETS, id, dbRequest) as Promise<O<Ticket>>
        }

        const loadParentTickets = (
            parentId: string,
            dbRequest = false
        ) => {
            const { loadFiltered } = loaders(companyId)
            return loadFiltered(TableName.TICKETS, dbRequest, { parentId }) as Promise<Ticket[]>
        }

        const loadTickets = (ids: string[]) => {
            const { loadByIds } = loaders()
            return loadByIds(TableName.TICKETS, ids) as Promise<Ticket[]>
        }

        const loadTicketsByParents = async (parentIds: string[]): Promise<Ticket[]> => {
            const tickets = await Promise.all(parentIds.map(parentId => loadParentTickets(parentId)))
            return tickets.flat()
        }

        const loadTicketsWithProp = async (
            propName: string,
            dbRequest = false
        ) => {
            const { loadFiltered } = loaders(companyId)
            return loadFiltered(TableName.TICKETS, dbRequest, undefined, undefined, toList(propName)) as Promise<Ticket[]>
        }

        const loadParents = async (ticketIds: string[]) => {
            const tickets = await loadTickets(ticketIds)
            const purchaseIds = tickets.filter(ticket => ticket.parentTable === TableName.PURCHASE_ORDERS).map(ticket => ticket.parentId)
            const expenseIds = tickets.filter(ticket => ticket.parentTable === TableName.EXPENSES).map(ticket => ticket.parentId)
            const { loadPurchases } = purchaseLoaders()
            const purchases = await loadPurchases(purchaseIds)
            const { loadExpenses } = expenseLoaders()
            const expenses = await loadExpenses(expenseIds)
            return [...purchases, ...expenses]
        }

        const countTickets = (propName: string, propValue: string) => {
            const options = {
                valueFilters: [{
                    value: propValue,
                    prop: propName
                }],
                limit: 1
            }
            const { count2 } = loaders(companyId)
            return count2(TableName.TICKETS, options)
        }
        
        return {
            loadAllTickets,
            loadTicket,
            loadParentTickets,
            loadTickets,
            loadTicketsByParents,
            loadTicketsWithProp,
            loadParents,
            countTickets
        }
    }
}
